import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Loader2, QrCode, ScanLine, KeyRound } from "lucide-react";
import { api, formatApiError } from "../../api";
import { useMemberData } from "../../contexts/MemberDataContext";
import { MEMBER } from "../../constants/testIds";
import { toast } from "../ui/sonner";
import { QrScanner } from "../common/QrScanner";

// Reasons come straight from access_engine.decide_checkin — the same decision a front-desk
// check-in would produce, just phrased for the member rather than for staff.
const DENY_COPY = {
  NO_MEMBERSHIP: "You don't have a membership on record yet — the front desk can set one up.",
  EXPIRED: "Your membership has expired. Renew it and you're good to go.",
  SESSIONS_EXHAUSTED: "You've used all the sessions on your pack. Renew to keep going.",
  INACTIVE: "Your membership isn't active right now — the front desk can help.",
  ROAMING_NOT_ALLOWED: "Your plan doesn't cover this branch. The front desk can sort that out.",
  FROZEN: "Your membership is frozen. The front desk can unfreeze it for you.",
};

const cardStyle = { background: "#141414", border: "1px solid rgba(255,255,255,0.1)" };

function currentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("unsupported"));
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
  });
}

// The member's own rotating QR, for the front desk to scan. Re-fetched as it expires so what's
// on screen is always a code the server will still accept.
function MyQrPanel({ onBack }) {
  const tokenQ = useQuery({
    queryKey: ["member-checkin-token"],
    queryFn: async () => (await api.get("/member/checkin-token")).data,
    refetchInterval: (query) => ((query.state.data?.expires_in_seconds || 30) + 1) * 1000,
    refetchIntervalInBackground: false,
  });

  return (
    <div className="rounded-2xl p-5 space-y-4" style={cardStyle}>
      <p className="text-sm font-semibold text-center">Show this to the front desk</p>
      <div className="flex justify-center">
        {tokenQ.data ? (
          <div className="p-3 rounded-xl bg-white" data-testid={MEMBER.myQrCode}>
            <QRCodeSVG value={tokenQ.data.payload} size={190} level="M" />
          </div>
        ) : (
          <div className="h-[214px] flex items-center justify-center"><Loader2 size={22} className="animate-spin" color="#A1A1AA" /></div>
        )}
      </div>
      <p className="text-xs text-center" style={{ color: "#A1A1AA" }}>Refreshes automatically for your security</p>
      <button onClick={onBack} className="w-full text-xs font-semibold" style={{ color: "#A1A1AA" }}>Back</button>
    </div>
  );
}

export function CheckInCard() {
  const { reloadHome } = useMemberData();
  const reducedMotion = useReducedMotion();
  const [mode, setMode] = useState("idle"); // idle | scan | pin | myqr
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [needsBranch, setNeedsBranch] = useState(false);
  const [branchId, setBranchId] = useState("");

  const optionsQ = useQuery({
    queryKey: ["member-checkin-options"],
    queryFn: async () => (await api.get("/member/checkin-options")).data,
  });
  const options = optionsQ.data;

  const branchesQ = useQuery({
    queryKey: ["member-branches"],
    queryFn: async () => (await api.get("/member/branches")).data.branches,
    enabled: needsBranch,
  });

  const checkinMutation = useMutation({
    mutationFn: async ({ method, code }) => {
      const payload = { method };
      if (code) payload.code = code;
      if (branchId) payload.branch_id = branchId;
      // Only ask for location when the branch actually geofences — no reason to prompt for a
      // permission the check-in doesn't need.
      if (options?.geofence_required) {
        try {
          const pos = await currentPosition();
          payload.lat = pos.coords.latitude;
          payload.lng = pos.coords.longitude;
          payload.accuracy_m = pos.coords.accuracy;
        } catch {
          throw Object.assign(new Error("location"), {
            response: { data: { code: "LOCATION_BLOCKED", message: "This gym needs your location to check you in. Turn location on and try again." } },
          });
        }
      }
      return api.post("/member/checkin", payload);
    },
    onSuccess: async (res) => {
      setResult(res.data);
      setMode("idle");
      setNeedsBranch(false);
      setPin("");
      if (res.data.decision === "allow") await reloadHome();
    },
    onError: (e) => {
      const err = formatApiError(e);
      if (err.code === "BRANCH_REQUIRED") {
        setNeedsBranch(true);
        setMode("idle");
        return;
      }
      toast.error(err.message);
    },
  });

  const { mutate } = checkinMutation;
  const handleScan = useCallback((decoded) => mutate({ method: "qr", code: decoded }), [mutate]);

  // A branch the member just picked changes which methods are on offer.
  useEffect(() => { if (branchId) optionsQ.refetch(); }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (result?.decision === "allow") {
    return (
      <motion.div
        initial={reducedMotion ? false : { scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        data-testid={MEMBER.checkinResult} className="rounded-2xl p-5 flex items-center gap-3"
        style={{ background: "rgba(50,215,75,0.12)", border: "1px solid rgba(50,215,75,0.35)" }}
      >
        <CheckCircle2 size={22} color="#32D74B" strokeWidth={1.5} className="shrink-0" />
        <div>
          <p className="text-sm font-semibold" style={{ color: "#32D74B" }}>You're checked in</p>
          <p className="text-xs mt-0.5" style={{ color: "#A1A1AA" }}>Have a great session.</p>
        </div>
      </motion.div>
    );
  }

  if (result) {
    return (
      <div data-testid={MEMBER.checkinResult} className="rounded-2xl p-5 space-y-3" style={{ background: "#141414", border: "1px solid rgba(214,32,43,0.35)" }}>
        <p className="text-sm font-semibold" style={{ color: "#D6202B" }}>Couldn't check you in</p>
        <p className="text-xs" style={{ color: "#D4D4D8" }}>{DENY_COPY[result.reason] || "The front desk can help you with this one."}</p>
        <button onClick={() => setResult(null)} className="text-xs font-semibold" style={{ color: "#A1A1AA" }}>Try again</button>
      </div>
    );
  }

  if (mode === "myqr") return <MyQrPanel onBack={() => setMode("idle")} />;

  const pinPanel = (
    <div className="space-y-2">
      <input
        data-testid={MEMBER.checkinPinInput} inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code"
        value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="w-full h-12 px-3 rounded-xl text-center text-2xl tracking-[0.4em] font-bold border bg-[#0A0A0A] border-white/10 text-white"
      />
      <button
        data-testid={MEMBER.checkinPinSubmit} disabled={pin.length < 4 || checkinMutation.isPending}
        onClick={() => mutate({ method: "pin", code: pin })}
        className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-50" style={{ background: "#D6202B" }}
      >
        {checkinMutation.isPending ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Check in"}
      </button>
    </div>
  );

  if (mode === "scan") {
    return (
      <div className="rounded-2xl p-5 space-y-3" style={cardStyle}>
        <QrScanner onScan={handleScan} onCancel={() => setMode("idle")} fallback={pinPanel} />
      </div>
    );
  }

  if (mode === "pin") {
    return (
      <div className="rounded-2xl p-5 space-y-3" style={cardStyle}>
        <p className="text-sm font-semibold">Enter the code shown at the gym</p>
        {pinPanel}
        <button onClick={() => setMode("idle")} className="w-full text-xs font-semibold" style={{ color: "#A1A1AA" }}>Back</button>
      </div>
    );
  }

  const manualAllowed = options ? options.manual : true;

  return (
    <div className="space-y-3">
      {needsBranch && (
        <div className="rounded-2xl p-4 space-y-2" style={cardStyle}>
          <p className="text-xs" style={{ color: "#A1A1AA" }}>Which branch are you at?</p>
          <select
            data-testid={MEMBER.checkinBranchSelect} value={branchId} onChange={(e) => setBranchId(e.target.value)}
            className="w-full h-9 px-3 rounded-md text-sm border bg-[#0A0A0A] border-white/10 text-white"
          >
            <option value="">Select a branch…</option>
            {(branchesQ.data || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}

      {manualAllowed ? (
        <button
          data-testid={MEMBER.checkinButton}
          disabled={checkinMutation.isPending || (needsBranch && !branchId)}
          onClick={() => mutate({ method: "manual" })}
          className="w-full rounded-2xl py-4 font-heading text-lg font-bold text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
          style={{ background: "#D6202B", boxShadow: "0 8px 24px rgba(214,32,43,0.3)" }}
        >
          {checkinMutation.isPending ? <Loader2 size={18} className="animate-spin" /> : "I'm here — check in"}
        </button>
      ) : (
        <button
          data-testid={MEMBER.checkinScanButton}
          disabled={needsBranch && !branchId}
          onClick={() => setMode("scan")}
          className="w-full rounded-2xl py-4 font-heading text-lg font-bold text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
          style={{ background: "#D6202B", boxShadow: "0 8px 24px rgba(214,32,43,0.3)" }}
        >
          <ScanLine size={18} /> Scan to check in
        </button>
      )}

      <div className="grid grid-cols-3 gap-2">
        {manualAllowed && (
          <MethodChip testId={MEMBER.checkinScanButton} icon={ScanLine} label="Scan QR" onClick={() => setMode("scan")} />
        )}
        <MethodChip testId={MEMBER.checkinPinButton} icon={KeyRound} label="Enter PIN" onClick={() => setMode("pin")} />
        <MethodChip testId={MEMBER.myQrButton} icon={QrCode} label="Show my QR" onClick={() => setMode("myqr")} />
      </div>
    </div>
  );
}

function MethodChip({ testId, icon: Icon, label, onClick }) {
  return (
    <button
      data-testid={testId} onClick={onClick}
      className="rounded-xl py-3 flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
      style={cardStyle}
    >
      <Icon size={16} color="#D6202B" strokeWidth={1.5} />
      <span className="text-[11px] font-semibold" style={{ color: "#D4D4D8" }}>{label}</span>
    </button>
  );
}
